import type { LineSeriesOption, YAXisComponentOption } from 'echarts';

import type { TimeSeries, ValueAxisScales } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';
import type { ValueRange } from '@/shared/charts/time-series-chart/value-axis/ValueRange.types';
import { getAxisGroupId } from '@/shared/charts/time-series-chart/value-axis/resolveValueAxisRanges';

import { PLOT_GRID_INDEX } from '../config/chartLayout';
import { lineVisuals } from '../config/chartVisuals';
import type { LineViewport } from '../types/LineViewport.types';
import { getLineBucketCount } from './level-of-detail/getLineBucketCount';
import { type LineSeriesData, getRenderableLineSeriesData } from './level-of-detail/lineSeriesDataCache';
import { getGroupAxis, resolveGroupAxes } from './resolveGroupAxes';

/** A point the line itself cannot show, because it has no neighbour to join (see buildLonePointsOption). */
export type LonePointMarker = {
  timestamp: number;
  /** Height within the plot, 0 at the bottom and 1 at the top, on the value axis of its line. */
  position: number;
  color: string | undefined;
  opacity: number;
};

type LineSeriesOptions = {
  yAxis: YAXisComponentOption[];
  series: LineSeriesOption[];
  lonePoints: LonePointMarker[];
};

type BuildLineSeriesOptionParams = {
  series: TimeSeries[];
  highlightedSeriesIds?: string[];
  /** Window and width the lines are thinned for (see LineViewport). */
  viewport: LineViewport;
  /** Lines allowed to show their symbols (see getSymbolSeriesIds). */
  symbolSeriesIds: ReadonlySet<string>;
  /** Shaping of the group axes (see TimeSeriesChartProps.valueAxisScales). */
  valueAxisScales?: ValueAxisScales;
  theme: TimeSeriesChartTheme;
};

type LineEmphasis = 'none' | 'highlighted' | 'dimmed';

function getLineEmphasis(seriesId: string, highlightedSeriesIds: Set<string>): LineEmphasis {
  if (highlightedSeriesIds.size === 0) {
    return 'none';
  }

  return highlightedSeriesIds.has(seriesId) ? 'highlighted' : 'dimmed';
}

/** Placed by the range of the line's value axis, so a marker sits exactly where its point is. */
function getLonePointMarkers(
  lineSeriesData: LineSeriesData,
  { min, max }: ValueRange,
  color: string | undefined,
  emphasis: LineEmphasis
): LonePointMarker[] {
  const opacity = emphasis === 'dimmed' ? lineVisuals.dimmedOpacity : lineVisuals.defaultOpacity;

  return lineSeriesData.lonePoints.map(([timestamp, value]) => ({
    timestamp,
    position: (value - min) / (max - min),
    color,
    opacity,
  }));
}

/**
 * A hidden axis spanning exactly the given range, margin included: ECharts adds no padding of its
 * own, so the lone-point heights computed from the same range line up with the lines.
 */
function buildValueAxis({ min, max }: ValueRange): YAXisComponentOption {
  return {
    type: 'value',
    gridIndex: PLOT_GRID_INDEX,
    min,
    max,
    show: false,
    scale: true,
    splitLine: { show: false },
  };
}

function buildLineStyle(color: string | undefined, emphasis: LineEmphasis): LineSeriesOption['lineStyle'] {
  switch (emphasis) {
    case 'highlighted':
      return {
        ...(color && { color }),
        width: lineVisuals.highlightedWidth,
        opacity: lineVisuals.defaultOpacity,
      };

    case 'dimmed':
      return {
        ...(color && { color }),
        width: lineVisuals.dimmedWidth,
        opacity: lineVisuals.dimmedOpacity,
      };

    case 'none':
      return {
        ...(color && { color }),
        width: lineVisuals.defaultWidth,
        opacity: lineVisuals.defaultOpacity,
      };

    default:
      return emphasis satisfies never;
  }
}

type BuildSeriesParams = {
  series: TimeSeries;
  lineSeriesData: LineSeriesData;
  yAxisIndex: number;
  emphasis: LineEmphasis;
  showSymbol: boolean;
  theme: TimeSeriesChartTheme;
};

function buildSeries({
  series,
  lineSeriesData,
  yAxisIndex,
  emphasis,
  showSymbol,
  theme,
}: BuildSeriesParams): LineSeriesOption {
  return {
    id: series.id,
    name: series.label,
    type: 'line',
    // ECharts 6 spelling; the older `triggerLineEvent` is deprecated there.
    triggerEvent: 'line',
    yAxisIndex,
    symbol: 'circle',
    showSymbol,
    symbolSize: lineVisuals.symbolSize,
    itemStyle: {
      color: series.color,
      borderColor: theme.symbol.borderColor,
      borderWidth: lineVisuals.symbolBorderWidth,
    },
    z: emphasis === 'highlighted' ? lineVisuals.highlightedZ : lineVisuals.z,
    data: lineSeriesData.data,
    emphasis: {
      disabled: true,
    },
    lineStyle: buildLineStyle(series.color, emphasis),
  };
}

/**
 * One ECharts line per neutral series, in series order; one hidden value axis per axis group (see
 * TimeSeries.axisGroupId and resolveGroupAxes), in order of first appearance, indexed from 0 as an
 * option section (see composeOptionSections); and the lone points of the lines that show no
 * symbols of their own.
 *
 * Built in two passes: the axis of a group depends on every line in it, so all lines are thinned and
 * measured first, and only then given their axis index and lone-point heights.
 */
export function buildLineSeriesOption({
  series,
  highlightedSeriesIds = [],
  viewport,
  symbolSeriesIds,
  valueAxisScales,
  theme,
}: BuildLineSeriesOptionParams): LineSeriesOptions {
  const highlightedSeriesIdSet = new Set(highlightedSeriesIds);
  const bucketCount = getLineBucketCount(viewport.plotWidthPx);

  const measuredLines = series.map((timeSeries) => ({
    timeSeries,
    groupId: getAxisGroupId(timeSeries),
    lineSeriesData: getRenderableLineSeriesData(timeSeries, viewport.visibleRange, bucketCount),
  }));

  const axesByGroupId = resolveGroupAxes(
    measuredLines.map(({ groupId, lineSeriesData }) => ({
      groupId,
      minValue: lineSeriesData.minValue,
      maxValue: lineSeriesData.maxValue,
    })),
    valueAxisScales
  );

  const drawnLines = measuredLines.map(({ timeSeries, groupId, lineSeriesData }) => {
    const { yAxisIndex, range } = getGroupAxis(axesByGroupId, groupId);
    const emphasis = getLineEmphasis(timeSeries.id, highlightedSeriesIdSet);
    // A dimmed line is context only; its symbols would compete with the highlighted line.
    const showSymbol = symbolSeriesIds.has(timeSeries.id) && emphasis !== 'dimmed';

    return {
      option: buildSeries({ series: timeSeries, lineSeriesData, yAxisIndex, emphasis, showSymbol, theme }),
      // With symbols on, a lone point already has its own circle.
      lonePoints: showSymbol ? [] : getLonePointMarkers(lineSeriesData, range, timeSeries.color, emphasis),
    };
  });

  return {
    yAxis: [...axesByGroupId.values()].map(({ range }) => buildValueAxis(range)),
    series: drawnLines.map(({ option }) => option),
    lonePoints: drawnLines.flatMap(({ lonePoints }) => lonePoints),
  };
}
